import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Video, Radio, Monitor, Mic, Camera, Sparkles, Loader2, ArrowRight, Upload, ImageIcon, Trash2, Mail, User } from 'lucide-react';
import { API } from '../App';
import axios from 'axios';

const PRODUCTION_TYPES = [
  { id: 'podcast', label: 'Podcast Studio', icon: Mic, desc: 'Desk setup with mics, boom arms, and acoustic treatment' },
  { id: 'livestream', label: 'Livestream Studio', icon: Radio, desc: 'Streaming desk, cameras, monitors, and chat display' },
  { id: 'virtual-event', label: 'Virtual Event Setup', icon: Monitor, desc: 'Main camera, presentation screen, LED backdrop' },
  { id: 'church-broadcast', label: 'Church Broadcast', icon: Video, desc: 'Multi-camera, stage lighting, worship backdrop' },
  { id: 'full-professional', label: 'Full Professional Studio', icon: Camera, desc: 'Multi-cam, switcher, audio console, lighting grid' },
];

const PRODUCTION_LEVELS = [
  { id: 'starter', label: 'Starter', desc: 'Clean entry-level setup, budget-friendly', color: 'neon-blue' },
  { id: 'professional', label: 'Professional', desc: 'Mid-range gear, polished quality', color: 'amber-400' },
  { id: 'broadcast-level', label: 'Broadcast-Level', desc: 'High-end broadcast equipment', color: 'neon-pink' },
];

const STYLE_PREFERENCES = [
  { id: 'clean-modern', label: 'Clean / Modern', desc: 'Bright, minimalist, sleek' },
  { id: 'dark-cinematic', label: 'Dark / Cinematic', desc: 'Moody, RGB accents, dramatic' },
  { id: 'stage-event', label: 'Stage / Event Style', desc: 'Theatrical, branded backdrop' },
  { id: 'minimal-podcast', label: 'Minimal / Podcast', desc: 'Intimate, warm, cozy' },
];

const BREAKDOWN_ICONS = {
  cameras: Camera,
  lighting: Sparkles,
  audio: Mic,
  production_gear: Monitor,
  background_stage: Video,
};

const BREAKDOWN_LABELS = {
  cameras: 'Cameras',
  lighting: 'Lighting',
  audio: 'Audio',
  production_gear: 'Production Gear',
  background_stage: 'Background & Stage',
};

const TOTAL_STEPS = 5;

const StudioBuilder = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [roomImagePath, setRoomImagePath] = useState(null);
  const [roomImagePreview, setRoomImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [productionType, setProductionType] = useState('');
  const [productionLevel, setProductionLevel] = useState('');
  const [stylePreference, setStylePreference] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(1);
    setRoomImagePath(null);
    setRoomImagePreview(null);
    setIsUploading(false);
    setProductionType('');
    setProductionLevel('');
    setStylePreference('');
    setResult(null);
    setError('');
    setIsGenerating(false);
    setDragOver(false);
  };

  const fullReset = () => {
    reset();
    setUserName('');
    setUserEmail('');
  };

  const handleClose = () => {
    fullReset();
    onClose();
  };

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB.');
      return;
    }

    setError('');
    setIsUploading(true);

    const reader = new FileReader();
    reader.onload = (e) => setRoomImagePreview(e.target.result);
    reader.readAsDataURL(file);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/studio-builder/upload-room`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRoomImagePath(response.data.path);
    } catch (err) {
      console.error('Upload failed:', err);
      setError('Upload failed. You can skip this step or try again.');
      setRoomImagePreview(null);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  }, [handleFileUpload]);

  const removeImage = () => {
    setRoomImagePath(null);
    setRoomImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleGenerate = async () => {
    if (!userName.trim() || !isValidEmail(userEmail)) {
      setError('Please enter your name and a valid email address.');
      return;
    }

    setStep(6);
    setIsGenerating(true);
    setError('');

    try {
      const response = await axios.post(`${API}/studio-builder/generate`, {
        production_type: productionType,
        production_level: productionLevel,
        style_preference: stylePreference,
        room_image_path: roomImagePath || null,
        name: userName.trim(),
        email: userEmail.trim(),
      });

      setResult(response.data);
      setStep(7);
    } catch (err) {
      console.error('Generation failed:', err);
      setError('Generation failed. Please try again.');
      setStep(5);
    } finally {
      setIsGenerating(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return true;
    if (step === 2) return !!productionType;
    if (step === 3) return !!productionLevel;
    if (step === 4) return !!stylePreference;
    if (step === 5) return userName.trim().length > 0 && isValidEmail(userEmail);
    return false;
  };

  const handleNext = () => {
    if (step === 5) {
      handleGenerate();
    } else if (canProceed()) {
      setStep(step + 1);
    }
  };

  if (!isOpen) return null;

  const stepLabel = step <= TOTAL_STEPS ? `Step ${step} of ${TOTAL_STEPS}` : '';
  const headerTitle = step <= TOTAL_STEPS ? 'Studio Builder' : step === 6 ? 'Generating...' : 'Your Studio Concept';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center"
        data-testid="studio-builder-modal"
      >
        <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={handleClose} />

        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto bg-obsidian border border-amber-400/30"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#FBBF24 #0A0A0A' }}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-obsidian border-b border-white/10 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold uppercase text-amber-400" style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.3)' }}>
                {headerTitle}
              </h2>
              {stepLabel && <p className="text-white/50 text-sm mt-1">{stepLabel}</p>}
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 flex items-center justify-center border border-white/20 text-white/60 hover:text-white hover:border-white/40 transition-all"
              data-testid="studio-builder-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress bar */}
          {step <= TOTAL_STEPS && (
            <div className="px-6 pt-4">
              <div className="h-1 bg-white/10 w-full">
                <motion.div
                  className="h-full bg-amber-400"
                  initial={{ width: '0%' }}
                  animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          )}

          <div className="p-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Upload Room Image */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="font-heading text-lg sm:text-xl font-bold uppercase text-white mb-2">
                    Upload Your Space
                  </h3>
                  <p className="text-white/50 text-sm mb-6">
                    Upload a photo of your room and we'll transform it into a studio concept. Or skip to generate from scratch.
                  </p>

                  {error && (
                    <div className="mb-4 p-3 border border-red-500/50 bg-red-500/10 text-red-400 text-sm" data-testid="upload-error">
                      {error}
                    </div>
                  )}

                  {!roomImagePreview ? (
                    <div
                      className={`border-2 border-dashed transition-all duration-300 p-8 sm:p-12 text-center cursor-pointer ${
                        dragOver
                          ? 'border-amber-400 bg-amber-400/10'
                          : 'border-white/20 hover:border-amber-400/50 bg-white/[0.02]'
                      }`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      data-testid="upload-dropzone"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0])}
                        data-testid="upload-input"
                      />
                      {isUploading ? (
                        <div className="flex flex-col items-center">
                          <Loader2 size={40} className="text-amber-400 animate-spin mb-3" />
                          <p className="text-white/60 text-sm">Uploading your image...</p>
                        </div>
                      ) : (
                        <>
                          <Upload size={40} className="text-white/30 mx-auto mb-4" />
                          <p className="text-white font-heading font-bold uppercase text-sm mb-2">
                            Drop your room photo here
                          </p>
                          <p className="text-white/40 text-xs">
                            or click to browse — JPEG, PNG, WebP up to 10MB
                          </p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="relative border border-amber-400/30 overflow-hidden" data-testid="upload-preview">
                      <img src={roomImagePreview} alt="Your room" className="w-full h-48 sm:h-64 object-cover" />
                      <div className="absolute top-3 right-3 flex gap-2">
                        {isUploading && (
                          <div className="w-8 h-8 flex items-center justify-center bg-black/70 border border-amber-400/50">
                            <Loader2 size={16} className="text-amber-400 animate-spin" />
                          </div>
                        )}
                        <button
                          onClick={removeImage}
                          className="w-8 h-8 flex items-center justify-center bg-black/70 border border-white/20 text-white/60 hover:text-red-400 hover:border-red-400/50 transition-all"
                          data-testid="upload-remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      {roomImagePath && (
                        <div className="absolute bottom-3 left-3 px-2 py-1 bg-black/70 border border-amber-400/40 text-amber-400 text-xs flex items-center gap-1">
                          <ImageIcon size={12} />
                          Room photo ready
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Step 2: Production Type */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="font-heading text-lg sm:text-xl font-bold uppercase text-white mb-2">
                    What type of production setup?
                  </h3>
                  <p className="text-white/50 text-sm mb-6">Select the studio type that fits your vision.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {PRODUCTION_TYPES.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setProductionType(type.id)}
                        className={`p-4 text-left border transition-all duration-300 group ${
                          productionType === type.id
                            ? 'border-amber-400 bg-amber-400/10'
                            : 'border-white/10 hover:border-amber-400/50 bg-white/[0.02]'
                        }`}
                        data-testid={`type-${type.id}`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 flex items-center justify-center border ${
                            productionType === type.id ? 'border-amber-400' : 'border-white/20'
                          }`}>
                            <type.icon size={20} className={productionType === type.id ? 'text-amber-400' : 'text-white/60'} />
                          </div>
                          <span className={`font-heading font-bold uppercase text-sm ${
                            productionType === type.id ? 'text-amber-400' : 'text-white'
                          }`}>
                            {type.label}
                          </span>
                        </div>
                        <p className="text-xs text-white/50 ml-[52px]">{type.desc}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 3: Production Level */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="font-heading text-lg sm:text-xl font-bold uppercase text-white mb-2">
                    Choose your production level
                  </h3>
                  <p className="text-white/50 text-sm mb-6">This determines equipment tier and complexity.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {PRODUCTION_LEVELS.map((level) => (
                      <button
                        key={level.id}
                        onClick={() => setProductionLevel(level.id)}
                        className={`p-5 text-center border transition-all duration-300 ${
                          productionLevel === level.id
                            ? `border-${level.color} bg-${level.color}/10`
                            : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                        }`}
                        data-testid={`level-${level.id}`}
                      >
                        <span className={`font-heading text-lg font-bold uppercase block mb-2 ${
                          productionLevel === level.id ? `text-${level.color}` : 'text-white'
                        }`}>
                          {level.label}
                        </span>
                        <p className="text-xs text-white/50">{level.desc}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 4: Style Preference */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="font-heading text-lg sm:text-xl font-bold uppercase text-white mb-2">
                    Pick your style
                  </h3>
                  <p className="text-white/50 text-sm mb-6">Choose the aesthetic for your studio concept.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {STYLE_PREFERENCES.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => setStylePreference(style.id)}
                        className={`p-4 text-left border transition-all duration-300 ${
                          stylePreference === style.id
                            ? 'border-amber-400 bg-amber-400/10'
                            : 'border-white/10 hover:border-amber-400/50 bg-white/[0.02]'
                        }`}
                        data-testid={`style-${style.id}`}
                      >
                        <span className={`font-heading font-bold uppercase text-sm block mb-1 ${
                          stylePreference === style.id ? 'text-amber-400' : 'text-white'
                        }`}>
                          {style.label}
                        </span>
                        <p className="text-xs text-white/50">{style.desc}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 5: Name & Email */}
              {step === 5 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="font-heading text-lg sm:text-xl font-bold uppercase text-white mb-2">
                    Where should we send your studio concept?
                  </h3>
                  <p className="text-white/50 text-sm mb-6">
                    We'll generate your concept now and also email it to you with a full equipment breakdown and next steps.
                  </p>

                  {error && (
                    <div className="mb-4 p-3 border border-red-500/50 bg-red-500/10 text-red-400 text-sm" data-testid="info-error">
                      {error}
                    </div>
                  )}

                  <div className="space-y-4 max-w-md mx-auto">
                    <div>
                      <label className="block text-xs text-white/50 uppercase tracking-wider mb-2 font-heading">Your Name</label>
                      <div className="relative">
                        <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                        <input
                          type="text"
                          value={userName}
                          onChange={(e) => setUserName(e.target.value)}
                          placeholder="Enter your full name"
                          className="w-full bg-white/[0.03] border border-white/15 text-white placeholder-white/25 px-10 py-3 text-sm focus:outline-none focus:border-amber-400/60 transition-colors"
                          data-testid="info-name-input"
                          onKeyDown={(e) => e.key === 'Enter' && document.querySelector('[data-testid="info-email-input"]')?.focus()}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-white/50 uppercase tracking-wider mb-2 font-heading">Email Address</label>
                      <div className="relative">
                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                        <input
                          type="email"
                          value={userEmail}
                          onChange={(e) => setUserEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full bg-white/[0.03] border border-white/15 text-white placeholder-white/25 px-10 py-3 text-sm focus:outline-none focus:border-amber-400/60 transition-colors"
                          data-testid="info-email-input"
                          onKeyDown={(e) => e.key === 'Enter' && canProceed() && handleGenerate()}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 border border-white/10 bg-white/[0.02] max-w-md mx-auto">
                    <p className="text-xs text-white/40 text-center">
                      Your studio concept will be delivered to your inbox along with equipment recommendations and information on how we can help bring your vision to life.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Step 6: Generating */}
              {step === 6 && (
                <motion.div
                  key="step6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-16"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="mb-6"
                  >
                    <Loader2 size={48} className="text-amber-400" />
                  </motion.div>
                  <h3 className="font-heading text-xl font-bold uppercase text-white mb-2">
                    Building Your Studio
                  </h3>
                  <p className="text-white/50 text-sm text-center max-w-md">
                    {roomImagePath
                      ? 'Transforming your space into a custom studio concept with camera placements, lighting, audio, and production gear...'
                      : 'Our AI is designing your custom studio concept with camera placements, lighting, audio, and production gear...'}
                  </p>
                  <motion.div
                    className="mt-6 flex gap-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-2 h-2 bg-amber-400 rounded-full"
                        animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                        transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                      />
                    ))}
                  </motion.div>
                </motion.div>
              )}

              {/* Step 7: Results */}
              {step === 7 && result && (
                <motion.div
                  key="step7"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Email confirmation */}
                  <div className="mb-6 p-4 border border-amber-400/30 bg-amber-400/5 flex items-center gap-3">
                    <Mail size={18} className="text-amber-400 flex-shrink-0" />
                    <p className="text-sm text-white/70">
                      We've also sent this concept to <span className="text-amber-400 font-semibold">{userEmail}</span> with full details and next steps.
                    </p>
                  </div>

                  {/* Generated Image */}
                  {result.image_base64 && (
                    <div className="mb-8 border border-amber-400/30 overflow-hidden">
                      <img
                        src={`data:image/png;base64,${result.image_base64}`}
                        alt="AI Generated Studio Concept"
                        className="w-full h-auto"
                        data-testid="studio-builder-result-image"
                      />
                    </div>
                  )}

                  {/* Equipment Breakdown */}
                  {result.breakdown && (
                    <div className="mb-8">
                      <h3 className="font-heading text-lg font-bold uppercase text-amber-400 mb-4" style={{ textShadow: '0 0 15px rgba(251, 191, 36, 0.3)' }}>
                        Equipment Breakdown
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {Object.entries(result.breakdown).map(([key, value]) => {
                          const Icon = BREAKDOWN_ICONS[key] || Monitor;
                          const label = BREAKDOWN_LABELS[key] || key;
                          return (
                            <div key={key} className="p-4 border border-white/10 bg-white/[0.02]" data-testid={`breakdown-${key}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Icon size={16} className="text-amber-400" />
                                <span className="font-heading font-bold uppercase text-sm text-white">{label}</span>
                              </div>
                              {value.summary && <p className="text-xs text-white/50 mb-2">{value.summary}</p>}
                              {value.items && (
                                <ul className="space-y-1">
                                  {value.items.map((item, i) => (
                                    <li key={i} className="text-xs text-white/70 flex items-start gap-1.5">
                                      <span className="text-amber-400 mt-0.5">-</span>
                                      <span>{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Disclaimer */}
                  <div className="mb-6 p-4 border border-white/10 bg-white/[0.02]">
                    <p className="text-xs text-white/40 italic">{result.disclaimer}</p>
                  </div>

                  {/* CTA */}
                  <div className="text-center p-6 border border-amber-400/20 bg-amber-400/5">
                    <h3 className="font-heading text-xl font-bold uppercase text-white mb-2">
                      Want us to bring this studio to life?
                    </h3>
                    <p className="text-sm text-white/60 mb-4">
                      We help you build a production environment that brings your message to life with excellence and purpose.
                    </p>
                    <a
                      href="https://kingdomvibestudios.hbportal.co/public/69a9c1506b994e0034214eb6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-neon btn-neon-gold inline-flex items-center gap-2 text-sm px-6 py-3"
                      data-testid="studio-builder-cta"
                    >
                      <span>Schedule a Consultation</span>
                      <ArrowRight size={16} />
                    </a>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      onClick={reset}
                      className="text-sm text-white/40 hover:text-amber-400 transition-colors underline"
                      data-testid="studio-builder-restart"
                    >
                      Generate another concept
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Navigation */}
          {step <= TOTAL_STEPS && (
            <div className="sticky bottom-0 bg-obsidian border-t border-white/10 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => step > 1 ? setStep(step - 1) : handleClose()}
                className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm"
                data-testid="studio-builder-back"
              >
                <ChevronLeft size={16} />
                {step > 1 ? 'Back' : 'Cancel'}
              </button>

              <div className="flex items-center gap-3">
                {step === 1 && !roomImagePreview && (
                  <button
                    onClick={() => setStep(2)}
                    className="text-white/40 hover:text-white/70 transition-colors text-sm underline"
                    data-testid="studio-builder-skip-upload"
                  >
                    Skip — use blank layout
                  </button>
                )}
                <button
                  onClick={handleNext}
                  disabled={step !== 1 && !canProceed()}
                  className={`flex items-center gap-2 font-heading font-bold uppercase text-sm px-6 py-3 border transition-all ${
                    (step === 1 || canProceed())
                      ? 'border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-obsidian'
                      : 'border-white/10 text-white/20 cursor-not-allowed'
                  }`}
                  data-testid="studio-builder-next"
                >
                  {step === TOTAL_STEPS ? 'Generate' : 'Next'}
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StudioBuilder;
